/**
 * E-commerce Service
 * Handles all database operations for the e-commerce management app
 */
import { supabase } from '@/lib/supabase-client';
// ============================================
// TRANSFORM FUNCTIONS (snake_case → camelCase)
// ============================================
function transformStore(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        organizationId: data.organization_id,
        name: data.name,
        slug: data.slug,
        description: data.description,
        domain: data.domain,
        logoUrl: data.logo_url,
        status: data.status,
        settings: data.settings || {},
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
function transformStoreMember(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        storeId: data.store_id,
        userId: data.user_id,
        role: data.role,
        permissions: data.permissions || {},
        joinedAt: data.joined_at,
    };
}
function transformStoreApiToken(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        storeId: data.store_id,
        token: data.token,
        name: data.name,
        description: data.description,
        lastUsedAt: data.last_used_at,
        expiresAt: data.expires_at,
        isActive: data.is_active,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
function transformProduct(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        storeId: data.store_id,
        name: data.name,
        description: data.description,
        price: parseFloat(data.price),
        compareAtPrice: data.compare_at_price ? parseFloat(data.compare_at_price) : undefined,
        costPerItem: data.cost_per_item ? parseFloat(data.cost_per_item) : undefined,
        sku: data.sku,
        barcode: data.barcode,
        weight: data.weight,
        purity: data.purity,
        category: data.category,
        collectionId: data.collection_id,
        stock: data.stock,
        trackInventory: data.track_inventory,
        minStock: data.min_stock,
        maxStock: data.max_stock,
        images: data.images || [],
        variants: data.variants || [],
        tags: data.tags || [],
        customFields: data.custom_fields || {},
        seoTitle: data.seo_title,
        seoDescription: data.seo_description,
        isActive: data.is_active,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
function transformOrderItem(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        orderId: data.order_id,
        productId: data.product_id,
        variantId: data.variant_id,
        quantity: data.quantity,
        priceAtPurchase: parseFloat(data.price_at_purchase),
        discount: parseFloat(data.discount || 0),
        subtotal: parseFloat(data.subtotal),
        fulfillmentStatus: data.fulfillment_status,
        createdAt: data.created_at,
        product: data.product ? transformProduct(data.product) : undefined,
    };
}
function transformOrder(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        storeId: data.store_id,
        customerId: data.customer_id,
        orderNumber: data.order_number,
        subtotal: parseFloat(data.subtotal),
        tax: parseFloat(data.tax || 0),
        shipping: parseFloat(data.shipping || 0),
        discount: parseFloat(data.discount || 0),
        total: parseFloat(data.total),
        status: data.status,
        fulfillmentStatus: data.fulfillment_status,
        paymentStatus: data.payment_status,
        paymentMethod: data.payment_method,
        stripePaymentIntentId: data.stripe_payment_intent_id,
        shippingAddress: data.shipping_address,
        billingAddress: data.billing_address,
        shippingMethod: data.shipping_method,
        trackingNumber: data.tracking_number,
        notes: data.notes,
        tags: data.tags || [],
        customFields: data.custom_fields || {},
        riskLevel: data.risk_level,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        customer: data.customer ? transformCustomer(data.customer) : undefined,
        items: data.items ? data.items.map(transformOrderItem) : undefined,
    };
}
function transformCustomer(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        storeId: data.store_id,
        userId: data.user_id,
        email: data.email,
        firstName: data.first_name,
        lastName: data.last_name,
        phone: data.phone,
        avatarUrl: data.avatar_url,
        addresses: data.addresses || [],
        defaultAddress: data.default_address,
        tags: data.tags || [],
        segment: data.segment,
        lifetimeValue: parseFloat(data.lifetime_value || 0),
        totalOrders: data.total_orders || 0,
        avgOrderValue: parseFloat(data.avg_order_value || 0),
        lastOrderAt: data.last_order_at,
        customFields: data.custom_fields || {},
        marketingConsent: data.marketing_consent || false,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
function transformWebinar(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        storeId: data.store_id,
        title: data.title,
        description: data.description,
        instructorName: data.instructor_name,
        instructorBio: data.instructor_bio,
        instructorImageUrl: data.instructor_image_url,
        date: data.date,
        time: data.time,
        durationMinutes: data.duration_minutes,
        timezone: data.timezone,
        imageUrl: data.image_url,
        level: data.level,
        maxCapacity: data.max_capacity,
        currentRegistrations: data.current_registrations,
        zoomLink: data.zoom_link,
        zoomMeetingId: data.zoom_meeting_id,
        recordingUrl: data.recording_url,
        status: data.status,
        isRecorded: data.is_recorded,
        tags: data.tags || [],
        customFields: data.custom_fields || {},
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
function transformCollection(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        storeId: data.store_id,
        name: data.name,
        description: data.description,
        slug: data.slug,
        imageUrl: data.image_url,
        sortOrder: data.sort_order,
        conditions: data.conditions || {},
        productCount: data.product_count || 0,
        isActive: data.is_active,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
function transformDiscount(data) {
    if (!data)
        return data;
    return {
        id: data.id,
        storeId: data.store_id,
        code: data.code,
        title: data.title,
        description: data.description,
        type: data.type,
        value: data.value ? parseFloat(data.value) : undefined,
        minPurchase: data.min_purchase ? parseFloat(data.min_purchase) : undefined,
        maxUses: data.max_uses,
        timesUsed: data.times_used || 0,
        customerEligibility: data.customer_eligibility,
        usageLimitPerCustomer: data.usage_limit_per_customer,
        appliesTo: data.applies_to,
        targetIds: data.target_ids || [],
        validFrom: data.valid_from,
        validUntil: data.valid_until,
        isActive: data.is_active,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
}
// ============================================
// UTILITY FUNCTIONS
// ============================================
function generateStoreToken() {
    // Generate a URL-safe token: "st_" prefix + random string
    const randomPart = crypto.getRandomValues(new Uint8Array(24)).reduce((acc, byte) => {
        return acc + byte.toString(16).padStart(2, '0');
    }, '');
    return `st_${randomPart}`;
}
// ============================================
// STORES SERVICE
// ============================================
export const EcommerceService = {
    // Get all stores for a user
    async getStoresForUser(userId) {
        const { data, error } = await supabase
            .from('stores')
            .select(`
        *,
        store_members!inner(user_id, role)
      `)
            .eq('store_members.user_id', userId);
        if (error)
            throw error;
        return (data || []).map(transformStore);
    },
    // Get single store
    async getStore(storeId) {
        const { data, error } = await supabase
            .from('stores')
            .select('*')
            .eq('id', storeId)
            .single();
        if (error)
            throw error;
        return transformStore(data);
    },
    // Create new store
    async createStore(organizationId, input, userId) {
        // Create store
        const { data: storeData, error: storeError } = await supabase
            .from('stores')
            .insert({
            organization_id: organizationId,
            name: input.name,
            slug: input.slug,
            description: input.description,
            domain: input.domain,
            logo_url: input.logoUrl,
            status: 'active',
            settings: {},
            created_by: userId,
        })
            .select()
            .single();
        if (storeError)
            throw storeError;
        // Note: User is automatically added as store owner by the database AFTER trigger
        // No need to manually insert into store_members here
        return transformStore(storeData);
    },
    // Generate API token for store
    async generateStoreToken(storeId, input, userId) {
        const token = generateStoreToken();
        const { data, error } = await supabase
            .from('store_api_tokens')
            .insert({
            store_id: storeId,
            token,
            name: input.name,
            description: input.description,
            expires_at: input.expiresAt,
            is_active: true,
            created_by: userId,
        })
            .select()
            .single();
        if (error)
            throw error;
        return transformStoreApiToken(data);
    },
    // Validate store API token
    async validateStoreToken(token) {
        // Check token exists and is active
        const { data: tokenData, error: tokenError } = await supabase
            .from('store_api_tokens')
            .select('store_id, expires_at')
            .eq('token', token)
            .eq('is_active', true)
            .single();
        if (tokenError || !tokenData)
            return null;
        // Check if token has expired
        if (tokenData.expires_at) {
            const expiryDate = new Date(tokenData.expires_at);
            if (expiryDate < new Date()) {
                return null;
            }
        }
        // Update last_used_at timestamp
        await supabase
            .from('store_api_tokens')
            .update({ last_used_at: new Date().toISOString() })
            .eq('token', token);
        // Get and return the store
        const store = await this.getStore(tokenData.store_id);
        return store;
    },
    // Get all tokens for a store
    async getStoreTokens(storeId) {
        const { data, error } = await supabase
            .from('store_api_tokens')
            .select('*')
            .eq('store_id', storeId)
            .order('created_at', { ascending: false });
        if (error)
            throw error;
        return (data || []).map(transformStoreApiToken);
    },
    // Revoke (deactivate) a store token
    async revokeStoreToken(tokenId) {
        const { error } = await supabase
            .from('store_api_tokens')
            .update({
            is_active: false,
            updated_at: new Date().toISOString(),
        })
            .eq('id', tokenId);
        if (error)
            throw error;
    },
    // Regenerate a store token (revoke old, create new)
    async regenerateStoreToken(tokenId, userId) {
        // Get original token info
        const { data: originalToken, error: getError } = await supabase
            .from('store_api_tokens')
            .select('store_id, name')
            .eq('id', tokenId)
            .single();
        if (getError || !originalToken)
            throw getError;
        // Revoke old token
        await this.revokeStoreToken(tokenId);
        // Create new token with same name
        return this.generateStoreToken(originalToken.store_id, {
            storeId: originalToken.store_id,
            name: `${originalToken.name} (regenerated)`,
        }, userId);
    },
    // ============================================
    // PRODUCTS SERVICE
    // ============================================
    async getProducts(storeId, options) {
        let query = supabase
            .from('ecommerce_products')
            .select('*')
            .eq('store_id', storeId);
        if (options?.search) {
            query = query.or(`name.ilike.%${options.search}%,sku.ilike.%${options.search}%`);
        }
        if (options?.category) {
            query = query.eq('category', options.category);
        }
        if (options?.collection) {
            query = query.eq('collection_id', options.collection);
        }
        if (options?.status === 'active') {
            query = query.eq('is_active', true);
        }
        else if (options?.status === 'low_stock') {
            query = query.lt('stock', supabase.rpc('get_min_stock'));
        }
        else if (options?.status === 'out_of_stock') {
            query = query.eq('stock', 0);
        }
        query = query
            .order('created_at', { ascending: false })
            .limit(options?.limit || 50);
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
        }
        const { data, error } = await query;
        if (error)
            throw error;
        return (data || []).map(transformProduct);
    },
    async getProduct(productId) {
        const { data, error } = await supabase
            .from('ecommerce_products')
            .select('*')
            .eq('id', productId)
            .single();
        if (error)
            throw error;
        return transformProduct(data);
    },
    async createProduct(storeId, product) {
        const { data, error } = await supabase
            .from('ecommerce_products')
            .insert({
            store_id: storeId,
            name: product.name,
            description: product.description,
            price: product.price,
            compare_at_price: product.compareAtPrice,
            cost_per_item: product.costPerItem,
            sku: product.sku,
            barcode: product.barcode,
            weight: product.weight,
            purity: product.purity,
            category: product.category,
            collection_id: product.collectionId,
            stock: product.stock,
            track_inventory: product.trackInventory,
            min_stock: product.minStock,
            max_stock: product.maxStock,
            images: product.images,
            variants: product.variants,
            tags: product.tags,
            custom_fields: product.customFields,
            seo_title: product.seoTitle,
            seo_description: product.seoDescription,
            is_active: product.isActive,
        })
            .select()
            .single();
        if (error)
            throw error;
        return transformProduct(data);
    },
    async updateProduct(productId, updates) {
        const { data, error } = await supabase
            .from('ecommerce_products')
            .update({
            name: updates.name,
            description: updates.description,
            price: updates.price,
            compare_at_price: updates.compareAtPrice,
            cost_per_item: updates.costPerItem,
            sku: updates.sku,
            barcode: updates.barcode,
            weight: updates.weight,
            purity: updates.purity,
            category: updates.category,
            collection_id: updates.collectionId,
            stock: updates.stock,
            track_inventory: updates.trackInventory,
            min_stock: updates.minStock,
            max_stock: updates.maxStock,
            images: updates.images,
            variants: updates.variants,
            tags: updates.tags,
            custom_fields: updates.customFields,
            seo_title: updates.seoTitle,
            seo_description: updates.seoDescription,
            is_active: updates.isActive,
            updated_at: new Date().toISOString(),
        })
            .eq('id', productId)
            .select()
            .single();
        if (error)
            throw error;
        return transformProduct(data);
    },
    async deleteProduct(productId) {
        const { error } = await supabase
            .from('ecommerce_products')
            .delete()
            .eq('id', productId);
        if (error)
            throw error;
    },
    // ============================================
    // ORDERS SERVICE
    // ============================================
    async getOrders(storeId, options) {
        let query = supabase
            .from('ecommerce_orders')
            .select(`
        *,
        customer:ecommerce_customers(*),
        items:ecommerce_order_items(
          *,
          product:ecommerce_products(*)
        )
      `)
            .eq('store_id', storeId);
        if (options?.status) {
            query = query.eq('status', options.status);
        }
        if (options?.paymentStatus) {
            query = query.eq('payment_status', options.paymentStatus);
        }
        if (options?.search) {
            query = query.ilike('order_number', `%${options.search}%`);
        }
        if (options?.dateFrom) {
            query = query.gte('created_at', options.dateFrom);
        }
        if (options?.dateTo) {
            query = query.lte('created_at', options.dateTo);
        }
        query = query
            .order('created_at', { ascending: false })
            .limit(options?.limit || 50);
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
        }
        const { data, error } = await query;
        if (error)
            throw error;
        return (data || []).map(transformOrder);
    },
    async getOrder(orderId) {
        const { data, error } = await supabase
            .from('ecommerce_orders')
            .select(`
        *,
        customer:ecommerce_customers(*),
        items:ecommerce_order_items(
          *,
          product:ecommerce_products(*)
        )
      `)
            .eq('id', orderId)
            .single();
        if (error)
            throw error;
        return transformOrder(data);
    },
    async createOrder(storeId, input) {
        // Calculate totals
        const products = await Promise.all(input.items.map((item) => this.getProduct(item.productId)));
        let subtotal = 0;
        input.items.forEach((item, index) => {
            subtotal += products[index].price * item.quantity;
        });
        const tax = subtotal * 0.08; // 8% tax
        const shipping = 10; // Fixed shipping for now
        const total = subtotal + tax + shipping;
        // Generate order number
        const orderNumber = `ORD-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
        // Create order
        const { data: orderData, error: orderError } = await supabase
            .from('ecommerce_orders')
            .insert({
            store_id: storeId,
            customer_id: input.customerId,
            order_number: orderNumber,
            subtotal,
            tax,
            shipping,
            discount: 0,
            total,
            status: 'pending',
            fulfillment_status: 'unfulfilled',
            payment_status: 'unpaid',
            shipping_address: input.shippingAddress,
            billing_address: input.billingAddress,
            shipping_method: input.shippingMethod,
            notes: input.notes,
            tags: input.tags,
        })
            .select()
            .single();
        if (orderError)
            throw orderError;
        // Create order items
        const { error: itemsError } = await supabase
            .from('ecommerce_order_items')
            .insert(input.items.map((item) => ({
            order_id: orderData.id,
            product_id: item.productId,
            variant_id: item.variantId,
            quantity: item.quantity,
            price_at_purchase: products[input.items.indexOf(item)].price,
            subtotal: products[input.items.indexOf(item)].price * item.quantity,
            fulfillment_status: 'unfulfilled',
        })));
        if (itemsError)
            throw itemsError;
        return this.getOrder(orderData.id);
    },
    async updateOrder(orderId, updates) {
        const { error } = await supabase
            .from('ecommerce_orders')
            .update({
            status: updates.status,
            fulfillment_status: updates.fulfillmentStatus,
            payment_status: updates.paymentStatus,
            tracking_number: updates.trackingNumber,
            notes: updates.notes,
            shipping_address: updates.shippingAddress,
            billing_address: updates.billingAddress,
            updated_at: new Date().toISOString(),
        })
            .eq('id', orderId);
        if (error)
            throw error;
        return this.getOrder(orderId);
    },
    // ============================================
    // CUSTOMERS SERVICE
    // ============================================
    async getCustomers(storeId, options) {
        let query = supabase
            .from('ecommerce_customers')
            .select('*')
            .eq('store_id', storeId);
        if (options?.segment) {
            query = query.eq('segment', options.segment);
        }
        if (options?.search) {
            query = query.or(`email.ilike.%${options.search}%,first_name.ilike.%${options.search}%,last_name.ilike.%${options.search}%`);
        }
        query = query
            .order('created_at', { ascending: false })
            .limit(options?.limit || 50);
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
        }
        const { data, error } = await query;
        if (error)
            throw error;
        return (data || []).map(transformCustomer);
    },
    async getCustomer(customerId) {
        const { data, error } = await supabase
            .from('ecommerce_customers')
            .select('*')
            .eq('id', customerId)
            .single();
        if (error)
            throw error;
        return transformCustomer(data);
    },
    async createCustomer(storeId, input) {
        const { data, error } = await supabase
            .from('ecommerce_customers')
            .insert({
            store_id: storeId,
            email: input.email,
            first_name: input.firstName,
            last_name: input.lastName,
            phone: input.phone,
            addresses: input.addresses || [],
            tags: input.tags,
            segment: 'new',
            custom_fields: input.customFields || {},
            marketing_consent: input.marketingConsent,
        })
            .select()
            .single();
        if (error)
            throw error;
        return transformCustomer(data);
    },
    async updateCustomer(customerId, updates) {
        const { error } = await supabase
            .from('ecommerce_customers')
            .update({
            email: updates.email,
            first_name: updates.firstName,
            last_name: updates.lastName,
            phone: updates.phone,
            addresses: updates.addresses,
            tags: updates.tags,
            custom_fields: updates.customFields,
            marketing_consent: updates.marketingConsent,
            updated_at: new Date().toISOString(),
        })
            .eq('id', customerId);
        if (error)
            throw error;
        return this.getCustomer(customerId);
    },
    // ============================================
    // WEBINARS SERVICE
    // ============================================
    async getWebinars(storeId, options) {
        let query = supabase
            .from('ecommerce_webinars')
            .select('*')
            .eq('store_id', storeId);
        if (options?.status) {
            query = query.eq('status', options.status);
        }
        if (options?.level) {
            query = query.eq('level', options.level);
        }
        query = query
            .order('date', { ascending: true })
            .limit(options?.limit || 50);
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
        }
        const { data, error } = await query;
        if (error)
            throw error;
        return (data || []).map(transformWebinar);
    },
    async getWebinar(webinarId) {
        const { data, error } = await supabase
            .from('ecommerce_webinars')
            .select('*')
            .eq('id', webinarId)
            .single();
        if (error)
            throw error;
        return transformWebinar(data);
    },
    async createWebinar(storeId, input) {
        const { data, error } = await supabase
            .from('ecommerce_webinars')
            .insert({
            store_id: storeId,
            title: input.title,
            description: input.description,
            instructor_name: input.instructorName,
            instructor_bio: input.instructorBio,
            instructor_image_url: input.instructorImageUrl,
            date: input.date,
            time: input.time,
            duration_minutes: input.durationMinutes,
            timezone: input.timezone,
            image_url: input.imageUrl,
            level: input.level,
            max_capacity: input.maxCapacity,
            current_registrations: 0,
            zoom_link: input.zoomLink,
            zoom_meeting_id: input.zoomMeetingId,
            status: 'scheduled',
            tags: input.tags,
            custom_fields: input.customFields || {},
        })
            .select()
            .single();
        if (error)
            throw error;
        return transformWebinar(data);
    },
    // ============================================
    // COLLECTIONS SERVICE
    // ============================================
    async getCollections(storeId, options) {
        let query = supabase
            .from('ecommerce_collections')
            .select('*')
            .eq('store_id', storeId);
        if (options?.search) {
            query = query.ilike('name', `%${options.search}%`);
        }
        query = query
            .order('created_at', { ascending: false })
            .limit(options?.limit || 50);
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
        }
        const { data, error } = await query;
        if (error)
            throw error;
        return (data || []).map(transformCollection);
    },
    async createCollection(storeId, input) {
        // Generate slug from name
        const slug = input.name
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]/g, '');
        const { data, error } = await supabase
            .from('ecommerce_collections')
            .insert({
            store_id: storeId,
            name: input.name,
            description: input.description,
            slug,
            image_url: input.imageUrl,
            sort_order: input.sortOrder || 'manual',
            product_count: 0,
            is_active: true,
        })
            .select()
            .single();
        if (error)
            throw error;
        return transformCollection(data);
    },
    async updateCollection(collectionId, updates) {
        const { data, error } = await supabase
            .from('ecommerce_collections')
            .update({
            name: updates.name,
            description: updates.description,
            image_url: updates.imageUrl,
            sort_order: updates.sortOrder,
            is_active: updates.isActive,
            updated_at: new Date().toISOString(),
        })
            .eq('id', collectionId)
            .select()
            .single();
        if (error)
            throw error;
        return transformCollection(data);
    },
    async deleteCollection(collectionId) {
        const { error } = await supabase
            .from('ecommerce_collections')
            .delete()
            .eq('id', collectionId);
        if (error)
            throw error;
    },
    // ============================================
    // DISCOUNTS SERVICE
    // ============================================
    async getDiscounts(storeId, options) {
        let query = supabase
            .from('ecommerce_discounts')
            .select('*')
            .eq('store_id', storeId);
        if (options?.search) {
            query = query.or(`code.ilike.%${options.search}%,title.ilike.%${options.search}%`);
        }
        if (options?.status !== undefined) {
            query = query.eq('is_active', options.status);
        }
        query = query
            .order('created_at', { ascending: false })
            .limit(options?.limit || 50);
        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
        }
        const { data, error } = await query;
        if (error)
            throw error;
        return (data || []).map(transformDiscount);
    },
    async createDiscount(storeId, input) {
        const { data, error } = await supabase
            .from('ecommerce_discounts')
            .insert({
            store_id: storeId,
            code: input.code,
            title: input.title,
            description: input.description,
            type: input.type,
            value: input.value,
            min_purchase: input.minPurchase,
            max_uses: input.maxUses,
            customer_eligibility: input.customerEligibility || 'all',
            usage_limit_per_customer: input.usageLimitPerCustomer,
            applies_to: input.appliesTo || 'all',
            target_ids: input.targetIds,
            valid_from: input.validFrom,
            valid_until: input.validUntil,
            is_active: input.isActive !== false,
            times_used: 0,
        })
            .select()
            .single();
        if (error)
            throw error;
        return transformDiscount(data);
    },
    async updateDiscount(discountId, updates) {
        const { data, error } = await supabase
            .from('ecommerce_discounts')
            .update({
            title: updates.title,
            description: updates.description,
            type: updates.type,
            value: updates.value,
            min_purchase: updates.minPurchase,
            max_uses: updates.maxUses,
            valid_from: updates.validFrom,
            valid_until: updates.validUntil,
            is_active: updates.isActive,
            updated_at: new Date().toISOString(),
        })
            .eq('id', discountId)
            .select()
            .single();
        if (error)
            throw error;
        return transformDiscount(data);
    },
    async deleteDiscount(discountId) {
        const { error } = await supabase
            .from('ecommerce_discounts')
            .delete()
            .eq('id', discountId);
        if (error)
            throw error;
    },
    // ============================================
    // INVENTORY SERVICE
    // ============================================
    async adjustInventory(productId, input) {
        // Get current stock
        const { data: product, error: getError } = await supabase
            .from('ecommerce_products')
            .select('stock, store_id')
            .eq('id', productId)
            .single();
        if (getError)
            throw getError;
        const newStock = Math.max(0, product.stock + input.quantityChange);
        // Update product stock
        const { error: updateError } = await supabase
            .from('ecommerce_products')
            .update({
            stock: newStock,
            updated_at: new Date().toISOString(),
        })
            .eq('id', productId);
        if (updateError)
            throw updateError;
        // Record inventory transfer
        const { error: transferError } = await supabase
            .from('ecommerce_inventory_transfers')
            .insert({
            store_id: product.store_id,
            product_id: productId,
            quantity_change: input.quantityChange,
            reason: input.reason,
            notes: input.notes,
            created_at: new Date().toISOString(),
        });
        if (transferError)
            throw transferError;
    },
    // ============================================
    // ANALYTICS SERVICE
    // ============================================
    async getDashboardStats(storeId) {
        // Parallel queries for stats
        const [{ data: revenueData, error: revenueError }, { count: orderCount, error: orderError }, { count: customerCount, error: customerError }, { count: lowStockCount, error: lowStockError }, { count: upcomingWebinarCount, error: webinarError },] = await Promise.all([
            supabase
                .from('ecommerce_orders')
                .select('total')
                .eq('store_id', storeId)
                .eq('payment_status', 'paid'),
            supabase
                .from('ecommerce_orders')
                .select('id', { count: 'exact' })
                .eq('store_id', storeId),
            supabase
                .from('ecommerce_customers')
                .select('id', { count: 'exact' })
                .eq('store_id', storeId),
            supabase
                .from('ecommerce_products')
                .select('id', { count: 'exact' })
                .eq('store_id', storeId)
                .lt('stock', 11), // Low stock if less than 10
            supabase
                .from('ecommerce_webinars')
                .select('id', { count: 'exact' })
                .eq('store_id', storeId)
                .eq('status', 'scheduled')
                .gte('date', new Date().toISOString().split('T')[0]),
        ]);
        if (revenueError || orderError || customerError || lowStockError || webinarError) {
            throw new Error('Failed to fetch dashboard stats');
        }
        const totalRevenue = revenueData?.reduce((sum, order) => sum + parseFloat(order.total), 0) || 0;
        const totalOrders = orderCount || 0;
        const totalCustomers = customerCount || 0;
        const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
        const lowStockProducts = lowStockCount || 0;
        const upcomingWebinars = upcomingWebinarCount || 0;
        return {
            totalRevenue,
            totalOrders,
            totalCustomers,
            avgOrderValue,
            lowStockProducts,
            upcomingWebinars,
        };
    },
    // ============================================
    // AUTO-CREATE E-COMMERCE BOARDS
    // ============================================
    /**
     * Automatically create all 4 e-commerce boards (Products, Orders, Customers, Webinars)
     * These boards appear in the board navigation and sync with e-commerce data
     */
    async createEcommerceBoardsForStore(storeId, workspaceId, userId) {
        const boardIds = {
            products: '',
            orders: '',
            customers: '',
            webinars: '',
        };
        // Helper function to convert column array to object format expected by Board component
        const columnsArrayToObject = (columns) => {
            return columns.reduce((acc, col) => {
                acc[col.id] = col;
                return acc;
            }, {});
        };
        // ============================================
        // PRODUCTS BOARD
        // ============================================
        const productSections = [
            { title: 'Active', color: '#10B981', position: 0 },
            { title: 'Low Stock', color: '#F59E0B', position: 1 },
            { title: 'Out of Stock', color: '#EF4444', position: 2 },
            { title: 'Archived', color: '#64748B', position: 3 },
        ];
        const productColumnsArray = [
            { id: 'product-name', name: 'Product', type: 'text', width: 250 },
            { id: 'sku', name: 'SKU', type: 'text', width: 120 },
            { id: 'price', name: 'Price', type: 'numbers', width: 100 },
            { id: 'stock', name: 'Stock', type: 'numbers', width: 100 },
            { id: 'collection', name: 'Collection', type: 'text', width: 150 },
            {
                id: 'group',
                name: 'Product Group',
                type: 'status',
                width: 150,
                options: productSections.map(section => ({
                    id: section.title.toLowerCase().replace(/\s+/g, '-'),
                    label: section.title,
                    color: section.color,
                    isDone: false,
                })),
            },
            { id: 'updated', name: 'Updated', type: 'date', width: 150 },
        ];
        const productColumnsConfig = columnsArrayToObject(productColumnsArray);
        const { data: productsBoard, error: productsError } = await supabase
            .from('boards')
            .insert({
            workspace_id: workspaceId,
            title: `Store Products - ${storeId.substring(0, 8)}`,
            description: 'Manage product inventory and details',
            view_type: 'table',
            column_order: ['product-name', 'sku', 'price', 'stock', 'collection', 'group', 'updated'],
            available_columns: ['product-name', 'sku', 'price', 'stock', 'collection', 'group', 'updated'],
            columns_config: JSON.stringify(productColumnsConfig),
            created_by: userId,
        })
            .select()
            .single();
        if (productsError)
            throw productsError;
        boardIds.products = productsBoard.id;
        // Create product board sections
        for (const section of productSections) {
            await supabase.from('board_groups').insert({
                board_id: boardIds.products,
                title: section.title,
                color: section.color,
                position: section.position,
            });
        }
        // ============================================
        // ORDERS BOARD
        // ============================================
        const orderSections = [
            { title: 'Pending', color: '#F59E0B', position: 0 },
            { title: 'Confirmed', color: '#3B82F6', position: 1 },
            { title: 'Processing', color: '#8B5CF6', position: 2 },
            { title: 'Shipped', color: '#10B981', position: 3 },
            { title: 'Delivered', color: '#06B6D4', position: 4 },
        ];
        const orderColumnsArray = [
            { id: 'order-number', name: 'Order #', type: 'text', width: 150 },
            { id: 'customer', name: 'Customer', type: 'person', width: 200 },
            { id: 'total', name: 'Total', type: 'numbers', width: 120 },
            {
                id: 'status',
                name: 'Status',
                type: 'status',
                width: 150,
                options: orderSections.map(section => ({
                    id: section.title.toLowerCase().replace(/\s+/g, '-'),
                    label: section.title,
                    color: section.color,
                    isDone: section.title === 'Delivered',
                })),
            },
            { id: 'payment-status', name: 'Payment', type: 'status', width: 150 },
            { id: 'fulfillment', name: 'Fulfillment', type: 'status', width: 150 },
            { id: 'date', name: 'Date', type: 'date', width: 150 },
            { id: 'tracking', name: 'Tracking', type: 'text', width: 150 },
        ];
        const orderColumnsConfig = columnsArrayToObject(orderColumnsArray);
        const { data: ordersBoard, error: ordersError } = await supabase
            .from('boards')
            .insert({
            workspace_id: workspaceId,
            title: `Store Orders - ${storeId.substring(0, 8)}`,
            description: 'Manage orders with kanban workflow',
            view_type: 'kanban',
            column_order: ['order-number', 'customer', 'total', 'status', 'payment-status', 'fulfillment', 'date', 'tracking'],
            available_columns: ['order-number', 'customer', 'total', 'status', 'payment-status', 'fulfillment', 'date', 'tracking'],
            columns_config: JSON.stringify(orderColumnsConfig),
            created_by: userId,
        })
            .select()
            .single();
        if (ordersError)
            throw ordersError;
        boardIds.orders = ordersBoard.id;
        // Create order board sections
        for (const section of orderSections) {
            await supabase.from('board_groups').insert({
                board_id: boardIds.orders,
                title: section.title,
                color: section.color,
                position: section.position,
            });
        }
        // ============================================
        // CUSTOMERS BOARD
        // ============================================
        const customerSections = [
            { title: 'New', color: '#3B82F6', position: 0 },
            { title: 'Regular', color: '#10B981', position: 1 },
            { title: 'VIP', color: '#8B5CF6', position: 2 },
            { title: 'At Risk', color: '#F59E0B', position: 3 },
            { title: 'Lost', color: '#EF4444', position: 4 },
        ];
        const customerColumnsArray = [
            { id: 'name', name: 'Name', type: 'text', width: 200 },
            { id: 'email', name: 'Email', type: 'text', width: 220 },
            { id: 'lifetime-value', name: 'LTV', type: 'numbers', width: 120 },
            { id: 'total-orders', name: 'Orders', type: 'numbers', width: 100 },
            {
                id: 'segment',
                name: 'Segment',
                type: 'status',
                width: 150,
                options: customerSections.map(section => ({
                    id: section.title.toLowerCase().replace(/\s+/g, '-'),
                    label: section.title,
                    color: section.color,
                    isDone: false,
                })),
            },
            { id: 'last-order', name: 'Last Order', type: 'date', width: 150 },
        ];
        const customerColumnsConfig = columnsArrayToObject(customerColumnsArray);
        const { data: customersBoard, error: customersError } = await supabase
            .from('boards')
            .insert({
            workspace_id: workspaceId,
            title: `Store Customers - ${storeId.substring(0, 8)}`,
            description: 'Track customer lifecycle and engagement',
            view_type: 'table',
            column_order: ['name', 'email', 'lifetime-value', 'total-orders', 'segment', 'last-order'],
            available_columns: ['name', 'email', 'lifetime-value', 'total-orders', 'segment', 'last-order'],
            columns_config: JSON.stringify(customerColumnsConfig),
            created_by: userId,
        })
            .select()
            .single();
        if (customersError)
            throw customersError;
        boardIds.customers = customersBoard.id;
        // Create customer board sections
        for (const section of customerSections) {
            await supabase.from('board_groups').insert({
                board_id: boardIds.customers,
                title: section.title,
                color: section.color,
                position: section.position,
            });
        }
        // ============================================
        // WEBINARS BOARD
        // ============================================
        const webinarSections = [
            { title: 'Draft', color: '#64748B', position: 0 },
            { title: 'Scheduled', color: '#3B82F6', position: 1 },
            { title: 'In Progress', color: '#F59E0B', position: 2 },
            { title: 'Completed', color: '#10B981', position: 3 },
            { title: 'Cancelled', color: '#EF4444', position: 4 },
        ];
        const webinarColumnsArray = [
            { id: 'title', name: 'Title', type: 'text', width: 250 },
            { id: 'date', name: 'Date', type: 'date', width: 150 },
            { id: 'instructor', name: 'Instructor', type: 'person', width: 180 },
            { id: 'level', name: 'Level', type: 'status', width: 120 },
            { id: 'registrations', name: 'Registered', type: 'numbers', width: 120 },
            { id: 'capacity', name: 'Capacity', type: 'numbers', width: 100 },
            {
                id: 'status',
                name: 'Status',
                type: 'status',
                width: 150,
                options: webinarSections.map(section => ({
                    id: section.title.toLowerCase().replace(/\s+/g, '-'),
                    label: section.title,
                    color: section.color,
                    isDone: section.title === 'Completed',
                })),
            },
        ];
        const webinarColumnsConfig = columnsArrayToObject(webinarColumnsArray);
        const { data: webinarsBoard, error: webinarsError } = await supabase
            .from('boards')
            .insert({
            workspace_id: workspaceId,
            title: `Store Webinars - ${storeId.substring(0, 8)}`,
            description: 'Schedule and manage webinar events',
            view_type: 'calendar',
            column_order: ['title', 'date', 'instructor', 'level', 'registrations', 'capacity', 'status'],
            available_columns: ['title', 'date', 'instructor', 'level', 'registrations', 'capacity', 'status'],
            columns_config: JSON.stringify(webinarColumnsConfig),
            created_by: userId,
        })
            .select()
            .single();
        if (webinarsError)
            throw webinarsError;
        boardIds.webinars = webinarsBoard.id;
        // Create webinar board sections
        for (const section of webinarSections) {
            await supabase.from('board_groups').insert({
                board_id: boardIds.webinars,
                title: section.title,
                color: section.color,
                position: section.position,
            });
        }
        return boardIds;
    },
};
